// @ts-nocheck
import * as React from 'react';
import { ChevronDown, Plus, Trash2, TicketPercent } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/auth/clerkShim';
import useDiscountCodeStore from '@/store/discountCodeStore';
import { toast } from 'sonner';

const formatOff = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

/**
 * Registration-scoped discount codes (ADR 0026): fixed cents off the payment
 * (per player on per-player products, once on per-team). Collapsed by default
 * — lazy-loads the code list on first open.
 */
export default function DiscountCodesCard({ registrationId }) {
  const { getToken } = useAuth();
  const { discountCodes, getDiscountCodes, createDiscountCode, updateDiscountCode, deleteDiscountCode } =
    useDiscountCodeStore((s) => ({
      discountCodes: s.discountCodes,
      getDiscountCodes: s.getDiscountCodes,
      createDiscountCode: s.createDiscountCode,
      updateDiscountCode: s.updateDiscountCode,
      deleteDiscountCode: s.deleteDiscountCode,
    }));

  const [open, setOpen] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);
  const [code, setCode] = React.useState('');
  const [amount, setAmount] = React.useState('');
  const [maxUses, setMaxUses] = React.useState('');
  const [expiresAt, setExpiresAt] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  const onOpenChange = (next) => {
    setOpen(next);
    if (next && !loaded) {
      setLoaded(true);
      getDiscountCodes(getToken)(registrationId);
    }
  };

  const onCreate = async () => {
    const cents = Math.round(parseFloat(amount) * 100);
    if (!code.trim()) return toast.error('Enter a code');
    if (!Number.isFinite(cents) || cents <= 0) return toast.error('Enter an amount greater than $0');
    setSaving(true);
    const created = await createDiscountCode(getToken)(registrationId, {
      code: code.trim(),
      amountOff: cents,
      maxRedemptions: maxUses.trim() ? parseInt(maxUses, 10) : null,
      expiresAt: expiresAt || null,
    });
    setSaving(false);
    if (created) {
      setCode(''); setAmount(''); setMaxUses(''); setExpiresAt('');
    }
  };

  const onToggleActive = (dc) => {
    updateDiscountCode(getToken)(registrationId, dc._id, { active: !dc.active });
  };

  const onDelete = (dc) => {
    if (window.confirm(`Delete code ${dc.code}? Teams that already applied it keep their locked discount.`)) {
      deleteDiscountCode(getToken)(registrationId, dc._id);
    }
  };

  return (
    <Collapsible open={open} onOpenChange={onOpenChange} className="rounded-lg bg-muted">
      <CollapsibleTrigger asChild>
        <button type="button" className="w-full flex items-center justify-between p-4 text-left">
          <span className="flex items-center gap-2 text-sm font-medium">
            <TicketPercent className="h-4 w-4" /> Discount Codes
            {loaded && discountCodes.length > 0 && (
              <Badge variant="secondary">{discountCodes.length}</Badge>
            )}
          </span>
          <ChevronDown className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="px-4 pb-4 space-y-3">
        <p className="text-xs text-muted-foreground">
          A fixed amount off this registration's payment: applied per player on per-player
          pricing, once per team on per-team pricing. Combines with early-bird / multi-team
          pricing. A discount that covers the full amount makes the registration free.
        </p>

        {/* Add form */}
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label className="mb-1 block text-xs font-medium">Code</label>
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase().replace(/\s/g, ''))}
              placeholder="SUMMER10"
              className="w-36 uppercase"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium">Amount off</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
              <Input
                type="number" step="0.01" min="0"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="10.00"
                className="w-28 pl-7"
              />
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium">Max uses <span className="text-muted-foreground">(optional)</span></label>
            <Input
              type="text" inputMode="numeric"
              value={maxUses}
              onChange={(e) => setMaxUses(e.target.value.replace(/[^0-9]/g, ''))}
              placeholder="∞"
              className="w-24"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium">Expires <span className="text-muted-foreground">(optional)</span></label>
            <Input
              type="date"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              className="w-40"
            />
          </div>
          <Button size="sm" onClick={onCreate} disabled={saving}>
            <Plus className="mr-1 h-4 w-4" /> Add Code
          </Button>
        </div>

        {/* Code list */}
        {discountCodes.length === 0 ? (
          <p className="text-sm text-muted-foreground italic">No discount codes yet.</p>
        ) : (
          <div className="divide-y rounded-md border bg-background">
            {discountCodes.map((dc) => (
              <div key={dc._id} className="flex flex-wrap items-center gap-3 px-3 py-2 text-sm">
                <span className="font-mono font-semibold">{dc.code}</span>
                <Badge variant="outline">−{formatOff(dc.amountOff)}</Badge>
                <span className="text-muted-foreground">
                  {dc.redemptionCount || 0}{dc.maxRedemptions ? ` / ${dc.maxRedemptions}` : ''} used
                </span>
                {dc.expiresAt && (
                  <span className="text-muted-foreground">
                    expires {new Date(dc.expiresAt).toLocaleDateString()}
                  </span>
                )}
                <span className="ml-auto flex items-center gap-2">
                  <Switch checked={!!dc.active} onCheckedChange={() => onToggleActive(dc)} />
                  <span className="text-xs text-muted-foreground">{dc.active ? 'Active' : 'Off'}</span>
                  <Button variant="ghost" size="icon" aria-label="Delete discount code" className="h-8 w-8" onClick={() => onDelete(dc)}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </span>
              </div>
            ))}
          </div>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}
